import {
  ReactNode,
  useRef,
  useEffect,
  useState,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import {
  Box,
  Textarea,
  Text,
  TextareaProps,
  Button,
  Icon,
  FormLabel,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const transformValueToPreview = (
  value: string,
  substitutions: SubstitutionItem[]
) => {
  let updatedValue = value;
  for (const { key, value } of substitutions) {
    updatedValue = updatedValue.replaceAll(key, value);
  }
  return updatedValue;
};

const transformValueToInternal = (
  value: string,
  substitutions: SubstitutionItem[]
) => {
  let updatedValue = value;
  for (const { key, value } of substitutions) {
    updatedValue = updatedValue.replaceAll(value, key);
  }
  return updatedValue;
};

type SubstitutionItem = {
  key: string;
  label: string;
  value: string;
};

export interface KYDHighlightTextAreaProps
  extends Omit<TextareaProps, "value" | "onChange"> {
  value?: string;
  onChange?: (value: string) => void;
  onChangePreview?: (value: string) => void;
  placeholder?: string;
  height?: string | number;
  substitutions?: SubstitutionItem[];
  regex?: RegExp;
  removePlaceholderOnSingleKey?: boolean;
  label?: string;
  isDisabled?: boolean;
}

export default function KYDHighlightTextArea({
  value = "",
  onChange,
  onChangePreview,
  substitutions = [],
  regex = /\{\{(.*?)\}\}/g,
  placeholder = "Enter text here...",
  height = 150,
  isDisabled = false,
  removePlaceholderOnSingleKey = false,
  label,
  ...props
}: KYDHighlightTextAreaProps) {
  const [previewValue, setPreviewValue] = useState(
    transformValueToPreview(value, substitutions)
  );
  const highlightRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Gradient colors
  const leftHighlightColor = "#DAEBFF";
  const rightHighlightColor = "#F8FBC3";

  // Scroll synchronization between textarea and highlight layer
  useEffect(() => {
    const textEl = textareaRef.current;
    if (!textEl) return;

    function syncScroll() {
      if (highlightRef.current) {
        highlightRef.current.scrollTop = textEl.scrollTop;
        highlightRef.current.scrollLeft = textEl.scrollLeft;
      }
    }

    textEl.addEventListener("scroll", syncScroll);
    return () => {
      textEl.removeEventListener("scroll", syncScroll);
    };
  }, []);

  const getAllMatches = (text: string, pattern: RegExp) => {
    const matches: RegExpExecArray[] = [];
    let match;
    while ((match = pattern.exec(text)) !== null) {
      matches.push(match);
    }
    return matches;
  };

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (!removePlaceholderOnSingleKey) return;
    if (e.key !== "Backspace" && e.key !== "Delete") return;

    const textEl = textareaRef.current;
    if (!textEl) return;

    const { selectionStart, selectionEnd } = textEl;
    if (selectionStart == null || selectionEnd == null) return;

    const highlightWords = Object.values(substitutions);
    if (!highlightWords.length) return;

    const pattern = new RegExp(`(${highlightWords.join("|")})`, "gi");

    // Find matches in the preview text
    const allMatches = getAllMatches(previewValue, pattern);
    if (!allMatches.length) return;

    // Check if the cursor is inside any match
    for (const m of allMatches) {
      const start = m.index!;
      const end = start + m[0].length;
      const insideMatch =
        selectionStart >= start &&
        selectionStart <= end &&
        selectionEnd >= start &&
        selectionEnd <= end;

      if (insideMatch) {
        e.preventDefault();

        // Remove that entire match from previewValue
        const newText = previewValue.slice(0, start) + previewValue.slice(end);

        // Update local states + parent
        const newPreviewValue = transformValueToPreview(newText, substitutions);
        setPreviewValue(newPreviewValue);
        onChangePreview?.(newPreviewValue);
        onChange?.(transformValueToInternal(newText, substitutions));
        // Move the cursor
        requestAnimationFrame(() => {
          textEl.setSelectionRange(start, start);
        });
        break;
      }
    }
  }
  function handleTextChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const newText = e.target.value;
    const newPreviewValue = transformValueToPreview(newText, substitutions);
    setPreviewValue(newPreviewValue);
    onChangePreview?.(newPreviewValue);
    onChange?.(transformValueToInternal(newText, substitutions));
  }

  /**
   * Splits the text by matched words and wraps them with a gradient background.
   * Everything else is rendered as normal text.
   */
  function renderHighlightedText(): ReactNode {
    if (!previewValue || !substitutions.length) return null;

    const escapedValues = substitutions.map((item) => escapeRegExp(item.value));
    const pattern = new RegExp(`(${escapedValues.join("|")})`, "gi");

    if (!pattern) {
      // If there's no pattern, render the entire text as is
      return <Text as="span">{previewValue}</Text>;
    }

    let result: ReactNode[] = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(previewValue)) !== null) {
      // Add the normal text before the match
      if (match.index > lastIndex) {
        result.push(
          <Text as="span" key={`normal-${lastIndex}`}>
            {previewValue.substring(lastIndex, match.index)}
          </Text>
        );
      }

      // Get the matched text and check if it's just whitespace
      const matchedText = match[0];
      if (matchedText.trim() === "") {
        // If it's just whitespace, don't highlight it
        result.push(
          <Text as="span" key={`space-${match.index}`}>
            {matchedText}
          </Text>
        );
      } else {
        // Highlight the matched part (non-whitespace)
        result.push(
          <Box
            as="span"
            key={`highlight-${match.index}`}
            display="inline"
            bgGradient={`linear(to-r, ${leftHighlightColor}, ${rightHighlightColor})`}
            borderRadius="4px"
            py={1}
            lineHeight="1"
          >
            {matchedText}
          </Box>
        );
      }

      lastIndex = match.index + match[0].length;
    }

    // Add any remaining text after the last match
    if (lastIndex < previewValue.length) {
      result.push(
        <Text as="span" key={`normal-${lastIndex}`}>
          {previewValue.substring(lastIndex)}
        </Text>
      );
    }

    return result;
  }

  const [showItems, setShowItems] = useState(false);

  const handleAddData = (key: string) => {
    // If the last character is a space, add the key without a space
    const lastChar = previewValue.slice(-1);
    const valueToAdd = lastChar === " " ? key : " " + key;
    const newValue = previewValue + valueToAdd;

    setPreviewValue(transformValueToPreview(newValue, substitutions));
    onChangePreview?.(transformValueToPreview(newValue, substitutions));
    onChange?.(transformValueToInternal(newValue, substitutions));
  };

  const containerVariants = {
    visible: {
      transition: {
        staggerChildren: 0.05,
      },
    },
    hidden: {},
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  return (
    <Box
      boxShadow="0px 0px 10px 0px #0000000D"
      position="relative"
      width="100%"
      border="1px solid #E6E6E6"
      px={4}
      py={3}
      borderRadius={"8px"}
    >
      {/* Data section with buttons for template variables */}
      <Box mb={3} display={substitutions.length > 0 ? "block" : "none"}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={showItems ? "visible" : "hidden"}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            height: showItems ? "fit-content" : "32px",
          }}
        >
          <Button
            onClick={() => setShowItems(!showItems)}
            fontWeight="400"
            py={4}
            px={4}
            size="sm"
            borderRadius="100px"
            border="1px solid rgba(0, 118, 255, 0.20)"
            background="white"
            boxShadow="0px 0px 10px 0px #C3DDFB"
            isDisabled={isDisabled}
          >
            Add Data
            <Icon as={showItems ? ChevronLeftIcon : ChevronRightIcon} />
          </Button>
          {substitutions.map(({ label, key }) => (
            <motion.div key={key} variants={itemVariants}>
              <Button
                isDisabled={!showItems || isDisabled}
                style={{
                  pointerEvents: showItems ? "auto" : "none",
                  cursor: showItems ? "pointer" : "default",
                }}
                py={4}
                px={4}
                onClick={() => handleAddData(key)}
                size="sm"
                fontWeight="400"
                borderRadius="100px"
                border="1px solid rgba(0, 118, 255, 0.20)"
                background="linear-gradient(0deg, rgba(255, 255, 255, 0.78), rgba(255, 255, 255, 0.78)), linear-gradient(90deg, #C3DDFB, #F8FBC3)"
              >
                {label}
              </Button>
            </motion.div>
          ))}
        </motion.div>
      </Box>
      <Box
        borderRadius="8px"
        position="relative"
        width="100%"
        // Use a fixed or minHeight – up to you. This can also be "minHeight" if you want it to grow.
        height={height}
        overflow="hidden"
      >
        {/* Highlight layer behind the textarea */}
        <Box
          ref={highlightRef}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          overflow="auto"
          fontFamily="inherit"
          fontSize="14px"
          fontWeight="400"
          lineHeight="2"
          letterSpacing="normal"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          overflowWrap="break-word"
          boxSizing="border-box"
          padding="12px 0px"
          margin="0"
          width="100%"
          height="100%"
          border="none"
          color="transparent"
          pointerEvents="none"
          style={{
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            fontSmooth: "always",
          }}
        >
          {previewValue ? (
            renderHighlightedText()
          ) : (
            <Text color="gray.400">{placeholder}</Text>
          )}
        </Box>
        {label && (
          <FormLabel
            fontSize="12px"
            fontWeight="400"
            color="#00000080"
            background="white"
            mb={2}
            backgroundColor={"white"}
            zIndex={100}
            w="full"
            position="absolute"
          >
            {label}
          </FormLabel>
        )}
        {/* The real textarea that the user types into */}
        <Textarea
          ref={textareaRef}
          value={previewValue}
          onChange={handleTextChange}
          isDisabled={isDisabled}
          onKeyDown={handleKeyDown}
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          fontFamily="inherit"
          fontSize="14px"
          fontWeight="400"
          lineHeight="2"
          letterSpacing="normal"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
          overflowWrap="break-word"
          boxSizing="border-box"
          padding="12px 0px"
          margin="0"
          width="100%"
          height="100%"
          border="none"
          bg="transparent"
          resize="none"
          overflow="auto"
          _focus={{ boxShadow: "none" }}
          placeholder=""
          style={{
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            fontSmooth: "always",
          }}
          {...props}
        />
      </Box>
    </Box>
  );
}
